import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Moon, LogOut, Send, Loader2, CheckCircle, Clock, ShieldAlert, Minus, Lock } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { getArabicOrdinal } from '@/lib/utils';
import type { TodayQuestionResponse } from '@/types';

export default function Competition() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionData, setQuestionData] = useState<TodayQuestionResponse | null>(null);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [aiConfirmed, setAiConfirmed] = useState(false);
  const [progressPulse, setProgressPulse] = useState(false);

  useEffect(() => {
    loadTodayQuestion();
  }, []);

  const loadTodayQuestion = async () => {
    try {
      const response = await api.getTodayQuestion();
      if (response.success) {
        setQuestionData(response.data || null);
      }
    } catch (err) {
      console.error('Failed to load question:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionData?.question || !answer.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await api.submitAnswer({
        question_id: questionData.question.id,
        answer_text: answer.trim(),
      });

      if (response.success) {
        // Update progress immediately
        setQuestionData(prev => prev ? {
          ...prev,
          answered_count: (prev.answered_count || 0) + 1,
          already_answered: true,
          answered_days: [...(prev.answered_days || []), prev.day],
        } : prev);
        // Trigger pulse animation on progress bar
        setProgressPulse(true);
        setTimeout(() => setProgressPulse(false), 1500);
        setSubmitted(true);
      } else {
        setError(response.error || 'حدث خطأ أثناء إرسال الإجابة');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'حدث خطأ أثناء إرسال الإجابة');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-gray-800 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Moon className="w-8 h-8 text-gold-500" />
            <span className="text-xl font-jazeera font-bold text-gold-500">رحلة عبر الزمن</span>
          </Link>

          <div className="flex items-center gap-4">
            <div className="text-left">
              <p className="text-cream font-medium">{user?.full_name}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
              title="تسجيل الخروج"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className={`card mb-8 transition-all duration-700 ${progressPulse ? 'ring-2 ring-gold-500/50 shadow-lg shadow-gold-500/20' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-cream">سجل المشاركة</h3>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-gold-500" />
                جاوب
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-orange-500" />
                لم يُجاوب
              </span>
              <span className="flex items-center gap-1">
                <Lock className="w-2.5 h-2.5 text-gray-600" />
                مُغلق
              </span>
            </div>
          </div>

          {questionData && (
            <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-10 gap-2">
              {Array.from({ length: questionData.total_questions || 20 }, (_, i) => i + 1).map((day) => {
                const currentDay = questionData.day || 0;
                const isUpcoming = day > currentDay;
                const isToday = day === currentDay;
                const isAnswered = questionData.answered_days?.includes(day) ?? false;

                return (
                  <div
                    key={day}
                    className={`flex flex-col items-center justify-center rounded-lg py-2 text-xs transition-all ${
                      isUpcoming
                        ? 'bg-gray-800/20 border border-gray-800'
                        : isToday
                          ? 'ring-2 ring-gold-500/50 bg-gray-800'
                          : isAnswered
                            ? 'bg-gray-800'
                            : 'bg-orange-500/5 border border-dashed border-orange-500/30'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mb-1 ${
                      isUpcoming
                        ? 'bg-gray-800/50'
                        : isAnswered
                          ? 'bg-gold-500/20'
                          : 'bg-orange-500/15'
                    }`}>
                      {isUpcoming ? (
                        <Lock className="w-3 h-3 text-gray-700" />
                      ) : isAnswered ? (
                        <span className="text-gold-500 text-[10px]">&#10003;</span>
                      ) : (
                        <Minus className="w-3 h-3 text-orange-400" />
                      )}
                    </div>
                    <span className={`font-medium ${
                      isUpcoming
                        ? 'text-gray-700'
                        : isToday
                          ? 'text-gold-500'
                          : isAnswered
                            ? 'text-gray-300'
                            : 'text-orange-400'
                    }`}>
                      {day}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* AI Warning Modal */}
        {questionData?.question && !questionData.already_answered && !submitted && !aiConfirmed && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
            <div className="card max-w-lg w-full text-center border border-gold-500/30 shadow-2xl">
              <ShieldAlert className="w-16 h-16 text-gold-500 mx-auto mb-4" />
              <h2 className="text-2xl font-jazeera font-bold text-cream mb-4">
                تنبيه مهم
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                يُمنع منعاً باتاً استخدام الذكاء الاصطناعي (مثل ChatGPT أو أي أداة مشابهة) للإجابة على أسئلة المسابقة.
              </p>
              <p className="text-gray-400 mb-8">
                المسابقة تهدف إلى اختبار معرفتك الشخصية. يرجى الاعتماد على نفسك فقط في الإجابة.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setAiConfirmed(true)}
                  className="btn-primary px-8 py-3 text-lg"
                >
                  أتعهد بعدم استخدام الذكاء الاصطناعي
                </button>
                <Link
                  to="/"
                  className="btn-secondary px-8 py-3 text-lg inline-flex items-center justify-center"
                >
                  العودة للرئيسية
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Question Section */}
        {!questionData?.question ? (
          <div className="card text-center py-12">
            <Clock className="w-16 h-16 text-gold-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-cream mb-2">
              {questionData?.message || 'المسابقة لم تبدأ بعد'}
            </h2>
            <p className="text-gray-400">
              ترقب سؤال اليوم قريباً!
            </p>
          </div>
        ) : questionData.already_answered || submitted ? (
          <div className="card text-center py-12">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-cream mb-2">
              تم إرسال إجابتك!
            </h2>
            <p className="text-gray-400">
              سيتم تقييم إجابتك وإضافة النقاط قريباً
            </p>
          </div>
        ) : !aiConfirmed ? (
          <div className="card text-center py-12">
            <ShieldAlert className="w-16 h-16 text-gold-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-cream mb-2">
              يرجى تأكيد التعهد أعلاه لعرض السؤال
            </h2>
          </div>
        ) : (
          <div className="card">
            {/* Day Badge */}
            <div className="inline-block bg-gold-500/10 border border-gold-500/30 rounded-full px-4 py-1 mb-4">
              <span className="text-gold-500 font-bold">
                اليوم {getArabicOrdinal(questionData.day)}
              </span>
            </div>

            {/* Question Image */}
            {questionData.question.image_url && (
              <div className="mb-6 rounded-lg overflow-hidden border border-gray-700">
                <img
                  src={questionData.question.image_url}
                  alt="صورة السؤال"
                  className="w-full h-auto"
                />
              </div>
            )}

            {/* Question Text */}
            <h2 className="text-2xl font-jazeera font-bold text-cream mb-6 whitespace-pre-line">
              {questionData.question.question_text.replace(/\\n/g, '\n')}
            </h2>

            {/* Hint */}
            {questionData.question.hint && (
              <p className="text-gray-400 text-sm mb-6 bg-gray-800/50 rounded-lg p-4">
                💡 تلميح: {questionData.question.hint}
              </p>
            )}

            {/* Answer Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  إجابتك
                </label>
                <textarea
                  required
                  rows={4}
                  className="input-field resize-none"
                  placeholder="اكتب إجابتك هنا..."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                />
              </div>

              <div className="flex items-center justify-end">
                <button
                  type="submit"
                  disabled={submitting || !answer.trim()}
                  className="btn-primary flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      إرسال الإجابة
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
